import CustomField from "components/Common/CustomField"
import CustomLabel from "components/Common/CustomLabel"
import BackButton from "components/Shared/BackButton"
import * as Yup from "yup"
import { ErrorMessage, Field, Form, Formik } from "formik"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"
import {
  Card,
  CardBody,
  Col,
  Container,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap"
import Flatpickr from "react-flatpickr"
import "flatpickr/dist/themes/material_blue.css"
import Select from "react-select"
import { useSelector } from "react-redux"
import {
  getDropdownData,
  getSystemNationalities,
  getStationShiftDropdownList,
} from "store/actions/commonAction"
import {
  arabicNameOrNumberPattern,
  englishNameOrNumberPattern,
  formatDateForAPI,
  getLocalizedName,
} from "components/Common/Utils"
import CustomSingleSelect from "components/Shared/CustomSingleSelect"
import MatchedCandidates from "./MatchedCandidates"
import { showAlert } from "store/reducers/alertReducer"
import {
  createTrialOperation,
  getTrialOperationDetails,
  updateTrialOperation,
} from "../../store/actions/trialPeriodAction"
import { getUserLists } from "../../store/actions/userAction"
import CustomMultiSelect from "../../components/Shared/CustomMultiSelect"

const today = new Date().toISOString().split("T")[0] // Get today's date in YYYY-MM-DD

const initialValues = {
  title: "",
  title_ar: "",
  description: "",
  description_ar: "",
  start_date: today,
  end_date: today,
  total_capacity: "",
  city_ids: [],
  gender: "",
  is_saudi: "",
  employee_experience: "",
  employee_experience_as_per_document: "",
  national_ids: "",
  nationality_ids: [],
  note: "",
  is_pool: false,
  positions_ids: [],
  is_auto_accepted_invitation: false,
  station_ids: [],
  attendance_admin: null,
  shift: "",
}

const CreateTrialPeriod = ({ mode }) => {
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const uuid = searchParams.get("uuid")
  const {
    t,
    i18n: { language },
  } = useTranslation()

  const dispatch = useDispatch()

  const { loading, isLoading, loading2, trialOperationDetails } = useSelector(
    state => state.TrialPeriods
  )

  const { dropdownData, systemNationalities, work_shifts, stations } =
    useSelector(state => state.Common)
  const { usersList } = useSelector(state => state.Users)

  const formattedUserList = useMemo(() => {
    if (!usersList) return []

    return usersList.map(item => {
      return {
        label: item.full_name,
        value: item.id,
      }
    })
  }, [usersList])

  const stationsOptions = Object.entries(stations).map(([key, value]) => ({
    value: key,
    label: value[language === "en" ? "name" : "name_ar"],
  }))

  const shiftsOptions = Object.entries(work_shifts).map(([key, value]) => ({
    value: key,
    label: value[language === "en" ? "name" : "name_ar"],
  }))

  const [modal, setModal] = useState(false)
  const [prevTotalCapacity, setPrevTotalCapacity] = useState(0)
  const [formValues, setFormValues] = useState(initialValues)

  /*   uncomment this with the filters and matching candidates feature
  const [selectedCandidates, setSelectedCandidates] = useState([])
  const [selectAllPages, setSelectAllPages] = useState(true)
  const [selectCurrentPage, setSelectCurrentPage] = useState(false)
  const [uncheckedIds, setUncheckedIds] = useState([])*/

  useEffect(() => {
    if (uuid) {
      dispatch(getTrialOperationDetails(uuid))
    }
  }, [uuid])

  const populateData = () => {
    const {
      title,
      title_ar,
      description,
      description_ar,
      start_date,
      end_date,
      total_capacity,
      station,
      shift,
      gender,
      employee_experience,
      is_saudi,
      nationality_ids,
      city_ids,
      job_title_ids,
      do_not_notify,
      is_auto_accepted_invitation,
      national_ids,
      exclude_national_ids,
      attendance_by_admin_id,
      notes,
    } = trialOperationDetails
    const stationData = stationsOptions.find(item => parseInt(item.value) === station)
    setFormValues({
      title: title,
      title_ar: title_ar,
      start_date: mode === "add" ? today : start_date,
      end_date: mode === "add" ? today :  end_date,
      total_capacity: total_capacity,
      station_ids: [stationData],
      shift: shift,
      gender: gender,
      employee_experience: employee_experience,
      is_saudi: is_saudi,
      nationality_ids: nationality_ids,
      city_ids: city_ids,
      positions_ids: job_title_ids,
      do_not_notify: false,
      is_auto_accepted_invitation: is_auto_accepted_invitation,
      national_ids: [],
      attendance_admin: attendance_by_admin_id,
      note: notes,
    })
  }

  useEffect(() => {
    if (trialOperationDetails && uuid) {
      populateData()
    }
  }, [trialOperationDetails])

  useEffect(() => {
    const fetchDropdownApi = () => {
      const payload = {
        type: "all",
      }
      dispatch(getDropdownData(payload))
      dispatch(getSystemNationalities())
      dispatch(getUserLists())
      dispatch(getStationShiftDropdownList())
    }
    fetchDropdownApi()
  }, [])

  /* uncomment this with the filters and matching candidates feature
  const toggle = () => setModal(!modal)

  const genders = [
    {
      label: t("Select Option"),
      value: "",
    },
    {
      label: t("Male"),
      value: "male",
    },
    {
      label: t("Female"),
      value: "female",
    },
  ]

  const experiences = [
    {
      label: t("Select Option"),
      value: "",
    },
    {
      label: t("Experienced"),
      value: "1",
    },
    {
      label: t("New"),
      value: "2",
    },
  ]*/

  const handleOnSubmit = values => {
    const {
      title,
      title_ar,
      description,
      description_ar,
      start_date,
      end_date,
      total_capacity,
      city_ids,
      gender,
      is_saudi,
      employee_experience,
      national_ids,
      nationality_ids,
      note,
      is_pool,
      positions_ids,
      is_auto_accepted_invitation,
      station_ids,
      attendance_admin,
      shift,
    } = values

    const updatedStationIds = station_ids.map(item => item.value) || []

    const payload = {
      title,
      title_ar,
      description,
      description_ar,
      start_date: formatDateForAPI(start_date),
      end_date: formatDateForAPI(end_date),
      total_capacity,
      ...(mode === "update"
        ? { station: Number(updatedStationIds[0]) }
        : { stations: updatedStationIds }),
      shift,
      gender,
      employee_experience,
      is_saudi,
      nationality_ids,
      city_ids,
      job_title_ids: positions_ids,
      do_not_notify: false,
      is_auto_accepted_invitation,
      national_ids:
        national_ids && national_ids.length > 0
          ? national_ids
              ?.split(",")
              ?.map(id => id.trim()) // Remove spaces
              ?.filter(id => id !== "") // Remove empty values
          : [],
      exclude_national_ids: [],
      attendance_by_admin_id: attendance_admin,
      notes: note,
    }

    if (mode === "add") {
      dispatch(createTrialOperation(payload))
        .then(action => {
          const { statusCode, success, message } = action && action.payload
          if (statusCode === 200 && success) {
            dispatch(showAlert({ text: message, type: "success" }))
            navigate("/trial-operation")
          } else {
            dispatch(showAlert({ text: message, type: "error" }))
          }
        })
        .catch(error => {})
    } else if (mode === "update") {
      const data = {
        uuid: uuid,
        payload: payload,
      }
      dispatch(updateTrialOperation(data))
        .then(action => {
          const { statusCode, success, message } = action?.payload
          if (statusCode === 200 && success) {
            dispatch(showAlert({ text: message, type: "success" }))
            navigate(-1)
          } else {
            dispatch(showAlert({ text: message, type: "error" }))
          }
        })
        .catch(error => {})
    }
  }

  const facilitators = useMemo(() => {
    if (!usersList) return []

    const roles = ["attendance admin"]
    const filteredUsers = usersList.filter(item =>
      roles.some(role => item.role.toLowerCase().includes(role.toLowerCase()))
    )

    return filteredUsers.map(user => {
      return {
        label: user.full_name,
        value: user.id,
      }
    })
  }, [usersList])

  const validationSchema = Yup.object().shape({
    title: Yup.string().required(`${t("Field is required")}`)
      .matches(englishNameOrNumberPattern, t("Only English letters are allowed")),
    title_ar: Yup.string()
      .required(`${t("Field is required")}`)
      .matches(arabicNameOrNumberPattern, t("Only Arabic letters are allowed")),
    start_date:
      mode === "update"
        ? Yup.date()
        : Yup.date()
            .required(`${t("Field is required")}`)
            .min(
              new Date(new Date().setHours(0, 0, 0, 0)),
              t("Date must be today or later")
            )
            .test(
              "is-valid-date",
              t("Invalid date"),
              value => value instanceof Date && !isNaN(value)
            ),

    end_date: Yup.date()
      .required(`${t("Field is required")}`)
      .test(
        "is-after-start-date",
        t("End date must be today or later"),
        function (endDate) {
          const { start_date } = this.parent
          return (
            !start_date || !endDate || new Date(endDate) >= new Date(start_date) // Allow same date
          )
        }
      ),
    total_capacity: Yup.number()
      .required(`${t("Field is required")}`)
      .positive()
      .integer()
      .test(
        "is-not-decreasing",
        t("Total capacity cannot be less than the previous value"),
        function (value) {
          if (mode === "update" && prevTotalCapacity !== undefined) {
            return value >= prevTotalCapacity
          }
          return true
        }
      ),
    national_ids:
      mode === "add" &&
      Yup.string()
        .matches(
          /^(\d+(,\s?\d+)*)?$/,
          t("Invalid format. Use only numbers separated by commas.")
        ).nullable(),
    attendance_admin: Yup.string().nullable(),
    station_ids: Yup.array().min(1,
      t("At least one training material is required")
    ),
    shift: Yup.string().required(`${t("Field is required")}`),
  })

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div className="d-flex align-items-center mb-4">
            <BackButton />
            <h4 className="card-title mb-0">
              {mode === "add"
                ? t("Create Trial Operation")
                : t("Update Trial Operation")}
            </h4>
          </div>

          <Card className="theme_card">
            <CardBody>
              <Formik
                initialValues={formValues}
                enableReinitialize={true}
                validationSchema={validationSchema}
                onSubmit={handleOnSubmit}
              >
                {({
                  values,
                  errors,
                  touched,
                  setFieldValue,
                  isValid,
                  dirty,
                }) => (
                  <Form>
                    <Row>
                      <Col lg={6}>
                        <div className="form_field">
                          <CustomLabel
                            htmlFor="title"
                            label={t("Title (English)")}
                            isRequired={true}
                          />
                          <CustomField
                            name="title"
                            type="text"
                            placeholder={t("Title (English)")}
                            touched={touched}
                            errors={errors}
                          />
                        </div>
                      </Col>
                      <Col lg={6}>
                        <div className="form_field">
                          <CustomLabel
                            htmlFor="title_ar"
                            label={t("Title (Arabic)")}
                            isRequired={true}
                          />
                          <CustomField
                            name="title_ar"
                            type="text"
                            placeholder={t("Title (Arabic)")}
                            touched={touched}
                            errors={errors}
                          />
                        </div>
                      </Col>

                      <Col lg={4}>
                        <div className="form_field">
                          <CustomLabel
                            htmlFor="total_capacity"
                            label={t("Total Capacity")}
                            isRequired={true}
                          />
                          <CustomField
                            name="total_capacity"
                            type="number"
                            touched={touched}
                            errors={errors}
                          />
                        </div>
                      </Col>

                      <Col lg={4}>
                        <div className="form_field">
                          <CustomLabel
                            htmlFor="start_date"
                            label={t("Start Date")}
                            isRequired={true}
                          />
                          <Flatpickr
                            options={{
                              dateFormat: "d-m-Y",
                              // minDate: "today",
                            }} // Format: dd-mm-yyyy
                            value={
                              values.start_date
                                ? new Date(values.start_date)
                                : ""
                            }
                            onChange={selectedDates =>
                              setFieldValue("start_date", selectedDates[0])
                            }
                            placeholder={t("Select a date")}
                            className={`form-control ${
                              errors &&
                              errors.start_date &&
                              touched &&
                              touched.start_date
                                ? "is-invalid"
                                : ""
                            }`}
                            // disabled={mode === "update" && isPastDate}
                          />
                          <ErrorMessage
                            name={"start_date"}
                            component="div"
                            className="text-danger"
                          />
                        </div>
                      </Col>
                      <Col lg={4}>
                        <div className="form_field">
                          <CustomLabel
                            htmlFor="end_date"
                            label={t("End Date")}
                            isRequired={true}
                          />
                          <Flatpickr
                            options={{
                              dateFormat: "d-m-Y",
                              minDate: values.start_date
                                ? new Date(values.start_date)
                                : "today", // Prevents past dates
                            }}
                            value={
                              values.end_date ? new Date(values.end_date) : ""
                            }
                            onChange={selectedDates =>
                              setFieldValue("end_date", selectedDates[0])
                            }
                            placeholder={t("Select a date")}
                            className={`form-control ${
                              errors?.end_date && touched?.end_date
                                ? "is-invalid"
                                : ""
                            }`}
                            // disabled={
                            //   mode === "update" && isPastDate && !values.is_pool
                            // }
                          />
                          <ErrorMessage
                            name={"end_date"}
                            component="div"
                            className="text-danger"
                          />
                        </div>
                      </Col>

                      <Col lg={12}>
                        {/* Not needed for now will uncomment if required
                        <div className="form_field d-flex gap-2 align-items-center">
                          <input
                            type="checkbox"
                            checked={values.is_pool} // Use checked instead of value
                            onClick={() => {
                              if (mode === "update") {
                                return
                              }
                              const newIsPoolValue = !values.is_pool
                              setFieldValue("is_pool", newIsPoolValue)

                              // Reset other fields when is_pool is checked
                              if (newIsPoolValue) {
                                setFieldValue("city_ids", [])
                                setFieldValue("gender", "")
                                setFieldValue("nationality_ids", [])
                                setFieldValue("Nationalities", [])
                                setFieldValue("positions_ids", [])
                                setFieldValue("employee_experience", "")
                              }
                              setFieldValue(
                                "is_auto_accepted_invitation",
                                false
                              )
                            }}
                            style={{ height: "unset", width: "unset" }}
                            className="mb-1"
                            // disabled={mode === "update"}
                          />
                          <CustomLabel
                            htmlFor="is_pool"
                            label={t("Do not notify the candidates")}
                          />
                        </div>*/}

                        {/* <div className="form_field d-flex gap-2 align-items-center">
                          <input
                            type="checkbox"
                            checked={values.is_auto_accepted_invitation} // Use checked instead of value
                            onClick={() => {
                              if (mode === "update") {
                                return
                              }
                              setFieldValue(
                                "is_auto_accepted_invitation",
                                !values.is_auto_accepted_invitation
                              )

                              setFieldValue("is_pool", false)
                            }}
                            style={{ height: "unset", width: "unset" }}
                            className="mb-1"
                            // disabled={mode === "update"}
                          />
                          <CustomLabel
                            htmlFor="is_auto_accepted_invitation"
                            label={t("Auto accept the invite")}
                          />
                        </div> */}
                      </Col>

                      <Col lg={4}>
                        <div className="form_field">
                          <CustomLabel htmlFor="shift" label={t("Shift")} isRequired={true} />
                          <CustomSingleSelect
                            name="shift"
                            value={shiftsOptions.find(
                              item => item.value === values.shift
                            )}
                            options={shiftsOptions}
                            placeholder={t("Select Shift")}
                            onChange={e => setFieldValue(`shift`, e?.value)}
                            isDisabled={mode === "update" || values.is_pool}
                            isInvalid={touched?.shift && errors?.shift}
                          />

                          <ErrorMessage
                            name={"shift"}
                            component="div"
                            className="text-danger"
                          />
                        </div>
                      </Col>
                      <Col lg={4}>
                        <div className="form_field">
                          <CustomLabel htmlFor="station_ids" label={t("Station")}  isRequired={true} />
                          <CustomMultiSelect
                            name="station_ids"
                            value={values.station_ids}
                            options={stationsOptions}
                            placeholder={t("Select Station")}
                            onChange={e => setFieldValue(`station_ids`, e)}
                            isDisabled={mode === "update" || values.is_pool}
                            isInvalid={touched?.station_ids && errors?.station_ids}
                          />

                          <ErrorMessage
                            name={"station_ids"}
                            component="div"
                            className="text-danger"
                          />
                        </div>
                      </Col>

                      <Col lg={4}>
                        <div className="form_field">
                          <CustomLabel
                            htmlFor="attendance-admin"
                            label={t("Attendance Admin")}
                          />
                          <CustomSingleSelect
                            name="attendance_admin"
                            value={facilitators.find(
                              user => user.value === values.attendance_admin
                            )}
                            options={facilitators}
                            placeholder={t("Select Attendance Admin")}
                            onChange={e =>
                              setFieldValue(`attendance_admin`, e?.value)
                            }
                            isDisabled={(mode === "update" && trialOperationDetails?.attendance_by_admin_id)|| values.is_pool}
                            isInvalid={touched?.admin && errors?.admin}
                          />

                          <ErrorMessage
                            name={"attendance_admin"}
                            component="div"
                            className="text-danger"
                          />
                        </div>
                      </Col>

                      <Col lg={6}>
                        <div className="form_field">
                          <CustomLabel htmlFor="note" label={t("Note")} />
                          <Field
                            as="textarea"
                            name="note"
                            className="form-control"
                            placeholder={t("Enter Note")}
                            maxLength={250}
                          ></Field>
                        </div>
                      </Col>

                      {mode === "add" && (
                        <Col lg={6}>
                          <div className="form_field">
                            <CustomLabel
                              htmlFor="national_ids"
                              label={t("National ID / Iqama ID")}
                            />

                            <Field
                              as="textarea"
                              name="national_ids"
                              className="form-control"
                              placeholder={t(
                                "Please enter national Id/ Iqama Id: eg. 1234657890, 2345678910"
                              )}
                              readOnly={mode === "update"}
                            ></Field>
                            <ErrorMessage
                              name={"national_ids"}
                              component="div"
                              className="text-danger"
                            />
                          </div>
                        </Col>
                      )}

                      {/* Not needed until matching candidate functionality is restored
                      <div className="pt-2">
                        <h4 className="text-primary">Filter Conditions</h4>
                        <hr className="flex-grow-1 border-black" />
                      </div>

                      {!values.is_pool && (
                        <>
                          <Col lg={6}>
                            <div className="form_field">
                              <CustomLabel
                                htmlFor="city_ids"
                                label={t("Cities")}
                                // isRequired={true}
                              />
                              <Select
                                name={values.city_ids}
                                value={values.city_ids}
                                isMulti={true}
                                onChange={e => {
                                  setFieldValue("city_ids", e)
                                }}
                                options={dropdownData?.cities?.map(item => ({
                                  label: getLocalizedName(item, language),
                                  value: item.id,
                                }))}
                                className={`react-select-container`}
                                placeholder={`${" "} ${t("Select Cities")}`}
                                closeMenuOnSelect={false}
                                isDisabled={mode === "update" || values.is_pool}
                              />
                            </div>
                          </Col>

                          <Col lg={6}>
                            <div className="form_field">
                              <CustomLabel
                                htmlFor="gender"
                                label={t("Gender")}
                              />
                              <CustomSingleSelect
                                name="gender"
                                value={genders.find(
                                  item => item.value === values.gender
                                )}
                                options={genders}
                                placeholder={t("Select Gender")}
                                onChange={e =>
                                  setFieldValue(`gender`, e?.value)
                                }
                                isDisabled={mode === "update" || values.is_pool}
                              />

                              <ErrorMessage
                                name={"gender"}
                                component="div"
                                className="text-danger"
                              />
                            </div>
                          </Col>

                          <Col lg={6}>
                            <div className="form_field">
                              <CustomLabel
                                htmlFor="nationality_ids"
                                label={t("Nationalities")}
                                // isRequired={true}
                              />
                              <Select
                                value={values.nationality_ids}
                                isMulti
                                onChange={e => {
                                  setFieldValue("nationality_ids", e)
                                }}
                                isDisabled={mode === "update" || values.is_pool}
                                options={systemNationalities?.map(item => ({
                                  label: getLocalizedName(item, language),
                                  value: item.id,
                                }))}
                                className={`react-select-container`}
                                placeholder={`${" "} ${t(
                                  "Select Nationalities"
                                )}`}
                                closeMenuOnSelect={false}
                              />

                              <ErrorMessage
                                name={"nationality_ids"}
                                component="div"
                                className="text-danger"
                              />
                            </div>
                          </Col>

                          <Col lg={6}>
                            <div className="form_field">
                              <CustomLabel
                                htmlFor="employee_experience"
                                label={t("Employee Experience")}
                                // isRequired={true}
                              />
                              <CustomSingleSelect
                                name="employee_experience"
                                value={experiences.find(
                                  item =>
                                    item.value === values.employee_experience
                                )}
                                options={experiences}
                                placeholder={t("Select Employee Experience")}
                                onChange={e => {
                                  setFieldValue(`employee_experience`, e?.value)
                                }}
                                isDisabled={mode === "update" || values.is_pool}
                              />

                              <ErrorMessage
                                name={"employee_experience"}
                                component="div"
                                className="text-danger"
                              />
                            </div>
                          </Col>

                          <Col lg={6}>
                            <div className="form_field">
                              <CustomLabel
                                htmlFor="positions_ids"
                                label={t("Positions")}
                              />
                              <Select
                                name={values.positions_ids}
                                value={values.positions_ids}
                                isMulti={true}
                                onChange={e => {
                                  setFieldValue("positions_ids", e)
                                }}
                                options={dropdownData?.job_titles?.map(
                                  item => ({
                                    label: getLocalizedName(item, language),
                                    value: item.id,
                                  })
                                )}
                                className={`react-select-container`}
                                placeholder={`${" "} ${t(
                                  "Select positions"
                                )}`}
                                closeMenuOnSelect={false}
                                isDisabled={
                                  mode === "update" || values.is_pool
                                }
                              />
                            </div>
                          </Col>
                        </>
                      )}*/}

                      <Col lg={12}>
                        <div className="g_end mt-2 gap-3 cis_btn">
                          <button
                            className="main_btn disabled-button fit_content px-4"
                            type="button"
                            onClick={() => navigate(-1)}
                            // disabled={isLoading}
                          >
                            {t("Cancel")}
                          </button>

                          {/*<button*/}
                          {/*  className="main_btn fit_content px-4"*/}
                          {/*  type="button"*/}
                          {/*  onClick={toggle}*/}
                          {/*  disabled={!isValid || !dirty || mode === "update"}*/}
                          {/*>*/}
                          {/*  {t("View Matching Candidates")}*/}
                          {/*</button>*/}

                          <button
                            className="main_btn fit_content px-4"
                            type="submit"
                            disabled={
                              isLoading ||
                              !isValid ||
                              (dirty === false && mode != "update")
                            }
                          >
                            {mode === "add" ? t("Submit") : t("Update")}
                          </button>
                        </div>
                      </Col>
                    </Row>
                    {/* Not needed for now will uncomment if view matching candidates functionality is restored
                    <Modal
                      isOpen={modal}
                      toggle={toggle}
                      className="full_screen_modal"
                      backdrop="static"
                    >
                      <ModalHeader toggle={toggle}>
                        {t("Matched Candidates List")} 15
                         {!loading2 && totalCount ? `(${totalCount})` : ""}
                      </ModalHeader>
                      <ModalBody className="pt-0">
                        <MatchedCandidates
                          // showAll={showAll}
                          // permissions={permissions}
                          values={{ ...values }}
                          selectedCandidates={selectedCandidates}
                          setSelectedCandidates={setSelectedCandidates}
                          selectAllPages={selectAllPages}
                          setSelectAllPages={setSelectAllPages}
                          selectCurrentPage={selectCurrentPage}
                          setSelectCurrentPage={setSelectCurrentPage}
                          uncheckedIds={uncheckedIds}
                          setUncheckedIds={setUncheckedIds}
                        />
                      </ModalBody>
                    </Modal>*/}
                  </Form>
                )}
              </Formik>
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default CreateTrialPeriod
